/**
 * Automation-related CRM entity types: event automations, inbound/outbound
 * webhooks, validation rules. Split out of entityTypeRegistry.ts — see that
 * file for the merge point.
 */

import { getDisplayName } from '../../utils/getDisplayName';
import { AutomationDocumentation } from '../sections/automation-documentation';
import { AutomationScheduleSection } from '../sections/automation-schedule-section';
import { AutomationSubHeader } from '../sections/automation-sub-header';
import { AutomationSummary } from '../sections/automation-summary';
import { AutomationTriggersSection } from '../sections/automation-triggers-section';
import type { EntityTypeConfig } from './entityTypeRegistry';

/** Automation-related CRM entity types: automations, webhooks, validation rules. */
export const AUTOMATION_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  ahoy_event_automation: {
    displayName: 'Automation',
    pluralDisplayName: 'Automations',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    // ahoy_enabled: rendered as the sub-header toggle, not a field row.
    // ahoy_summary / ahoy_documentation: rendered by AutomationSummary.
    // Triggering properties: rendered as chips by AutomationTriggersSection.
    // Schema companions + script: consumed by the config/state form
    // decomposition and the script section — a raw row would double-render
    // each of them.
    hiddenProperties: [
      'ahoy_enabled',
      'ahoy_summary',
      'ahoy_documentation',
      'ahoy_triggering_entity_types',
      'ahoy_triggering_event_types',
      'ahoy_match_all_entity_types',
      'ahoy_config_schema',
      'ahoy_state_schema',
      'ahoy_script',
      // Rendered by the top zone's RecordMetaSection — a second copy in the
      // Overview tab's field list would just duplicate them.
      'ahoy_created_at',
      'ahoy_updated_at',
    ],
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    // The Overview tab's field list holds ONLY the automation's config
    // values: the inbound webhook endpoint renders up top
    // (AutomationTriggersSection), the outbound channel renders as a normal
    // editable field below, and the creator is in the meta block
    // (RecordMetaSection) — a raw inbound row here would double-render it.
    hiddenAssociations: [
      'ahoy_automation_to_webhook',
      'ahoy_entity_to_creator',
    ],
    routes: {
      list: '/:orgId/crm/ahoy_event_automation',
      detail: '/:orgId/crm/ahoy_event_automation/:id',
    },
    // An automation's behaviour lives in its script and config, which are not
    // meaningfully editable field-by-field — describing the change to the
    // assistant is the editing model. See useBuilderMode.
    supportsBuilderMode: true,
    typeSpecificPosition: 'before-overview',
    // Two-zone layout: top zone is basic (title, status badges + toggle,
    // description card); everything else lives behind tabs, ordered by how
    // often a reader wants them: what it does, what it did, then the
    // implementation detail. Runs is a LOCKED run-record view
    // (ahoy_automation_ran + legacy ahoy_event_processed rows) — the general
    // timeline never renders for automations. Tabs self-gate: Code hides for
    // built-ins (no script). Documentation nests at the bottom of Overview
    // (renders nothing when the prose is empty). Sections beyond that resolve
    // per-record from deriveAutomationCapabilities.
    slideoutTabs: ['overview', 'runs', 'testcases', 'code'],
    components: {
      summary: AutomationSummary,
      subHeader: AutomationSubHeader,
      typeSpecific: AutomationTriggersSection,
      documentation: AutomationDocumentation,
    },
  },

  ahoy_scheduled_automation: {
    displayName: 'Scheduled Automation',
    pluralDisplayName: 'Scheduled Automations',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    // Mirrors ahoy_event_automation, with the schedule replacing the
    // triggering-type properties: ahoy_schedule is rendered by
    // AutomationScheduleSection, so a raw row would double-render it.
    hiddenProperties: [
      'ahoy_enabled',
      'ahoy_summary',
      'ahoy_documentation',
      'ahoy_schedule',
      'ahoy_config_schema',
      'ahoy_state_schema',
      'ahoy_script',
      'ahoy_created_at',
      'ahoy_updated_at',
    ],
    // Same rationale as ahoy_event_automation: the inbound endpoint renders in
    // the top zone, the outbound channel as a normal field, the creator in the
    // meta block. (A scheduled automation has no inbound webhook trigger, but
    // the automation kind carries the outbound channel edge either way.)
    hiddenAssociations: [
      'ahoy_automation_to_webhook',
      'ahoy_entity_to_creator',
    ],
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    routes: {
      list: '/:orgId/crm/ahoy_scheduled_automation',
      detail: '/:orgId/crm/ahoy_scheduled_automation/:id',
    },
    supportsBuilderMode: true,
    typeSpecificPosition: 'before-overview',
    // Same two-zone layout and tab order as event automations — Run now
    // renders in the slideout footer via AutomationRunFooter (self-gating on
    // the scheduled type).
    slideoutTabs: ['overview', 'runs', 'testcases', 'code'],
    components: {
      summary: AutomationSummary,
      subHeader: AutomationSubHeader,
      typeSpecific: AutomationScheduleSection,
      documentation: AutomationDocumentation,
    },
  },

  ahoy_automation_webhook: {
    displayName: 'Inbound Webhook',
    pluralDisplayName: 'Inbound Webhooks',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    // No detail route: no hardcoded <CRMObjectDetailPage entityType='...'> route
    // is registered in dashboard.routes.tsx for this type. The slideout opened
    // from the list view is the intended record surface.
    routes: {
      list: '/:orgId/crm/ahoy_automation_webhook',
    },
  },

  ahoy_outbound_webhook: {
    displayName: 'Outbound Webhook',
    pluralDisplayName: 'Outbound Webhooks',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #64748B 0%, #94A3B8 100%)',
    getDisplayName,
    timelineConfig: {
      includeAssociatedEvents: false,
      associatedEventTypes: [],
      groupByDate: true,
      defaultFilters: {},
    },
    // No detail route: no hardcoded <CRMObjectDetailPage entityType='...'> route
    // is registered in dashboard.routes.tsx for this type. The slideout opened
    // from the list view is the intended record surface.
    routes: {
      list: '/:orgId/crm/ahoy_outbound_webhook',
    },
    // Internal system-managed marker — not useful to display.
    hiddenProperties: ['ahoy_hidden'],
    detailFieldOrder: [
      { type: 'property', key: 'ahoy_destination_url' },
      { type: 'property', key: 'ahoy_webhook_auth_method' },
      { type: 'property', key: 'ahoy_outbound_enabled' },
      { type: 'association', key: 'ahoy_outbound_channel_to_automation' },
      { type: 'association', key: 'ahoy_outbound_webhook_to_secret' },
      { type: 'timestamp', key: 'ahoy_created_at' },
      { type: 'timestamp', key: 'ahoy_updated_at' },
    ],
    // No createConfig: outbound webhooks are provisioned by the automation
    // authoring flow (apps/webhooks/services/outbound_provisioning.py), not a
    // generic create form. The config properties (ahoy_destination_url,
    // ahoy_webhook_auth_method, ahoy_display_name) are value_write_access: all
    // and any member may submit ahoy_object_created (D-10 in
    // apps/orgs/entities/permission_groups.yaml — mutations are then
    // creator-scoped), but the sanctioned entry point remains provisioning,
    // mirroring the inbound ahoy_automation_webhook (also no createConfig).
  },

  ahoy_validation_rule: {
    displayName: 'Validation Rule',
    pluralDisplayName: 'Validation Rules',
    color: 'red',
    bannerGradient: 'linear-gradient(135deg, #EF4444 0%, #F97316 100%)',
    getDisplayName,
    // The overview uses the normal inline editor for the plain fields
    // (Name / Message / Enabled). The target Entity Type is immutable
    // (value_update_access: none) so it renders read-only there, and the
    // When/Validate formulas are edited via the assistant by clicking their
    // per-field edit icon (see overview-section.tsx). Suppress the record
    // action kebab (Research / Enrich / Delete) — none apply to a rule.
    showRecordActionMenu: false,
    // Status badges only (Enforcing / Off / Not-enforcing-on-error) — the
    // errored state matters most: an errored rule is silently SKIPPED at
    // load, so the toggle-like Enabled row reads on while nothing is being
    // protected. No toggle here: rules keep their inline Enabled editor.
    components: {
      subHeader: AutomationSubHeader,
    },
  },
};
